import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Eye,
  Trash2,
  Printer,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Edit,
} from "lucide-react";
import Layout from "../components/Layout";
import { paymentsService } from "../services/api.service";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PaymentReceipt from "../components/payments/PaymentReceipt";
import PaymentDetailsModal from "../components/payments/PaymentDetails";
import AddPaymentModal from "../components/payments/addPayment";
import EditPaymentModal from "../components/payments/EditPaymentModal";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import logger from "../utils/logger";

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [viewPaymentDetails, setViewPaymentDetails] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentsPerPage] = useState(10);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    type: "all",
    dateFrom: null,
    dateTo: null,
  });

  // Validate payments data
  const validatePaymentsData = (data) => {
    if (!Array.isArray(data)) {
      logger.warn("Payments data is not an array:", data);
      return [];
    }
    return data;
  };

  // Load payments from API
  const loadPayments = async () => {
    try {
      setIsLoading(true);
      const response = await paymentsService.getPayments();
      const validatedData = validatePaymentsData(response.data);
      setPayments(validatedData);
      setFilteredPayments(validatedData);
    } catch (error) {
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadPayments();
  }, []);

  // Filter payments when filter criteria change
  useEffect(() => {
    if (Array.isArray(payments)) {
      filterPayments();
    } else {
      setFilteredPayments([]);
    }
  }, [searchInput, filters, payments]);

  // Filter payments based on criteria
  const filterPayments = () => {
    let filtered = [...payments];

    // Apply search term filter
    if (searchInput && searchInput.trim() !== "") {
      filtered = filtered.filter((payment) => {
        if (!payment || typeof payment !== "object") return false;

        // Search across multiple fields
        const searchTerm = searchInput.toLowerCase();

        // Check client name
        if (
          payment.client_name &&
          String(payment.client_name).toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        // Check description
        if (
          payment.description &&
          String(payment.description).toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        // Check case number
        if (
          payment.case_number &&
          String(payment.case_number).toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        // Check amount (convert to string for comparison)
        if (
          payment.amount &&
          String(payment.amount).toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        return false;
      });
    }

    // Apply status filter
    if (filters.status !== "all") {
      filtered = filtered.filter(
        (payment) => payment.status === filters.status
      );
    }

    // Apply type filter
    if (filters.type !== "all") {
      filtered = filtered.filter((payment) => payment.type === filters.type);
    }

    // Apply date range filters
    if (filters.dateFrom) {
      filtered = filtered.filter(
        (payment) => new Date(payment.date) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(
        (payment) => new Date(payment.date) <= new Date(filters.dateTo)
      );
    }

    setFilteredPayments(filtered);
  };

  const handleAddPayment = async (paymentData) => {
    try {
      await paymentsService.addPayment(paymentData);
      loadPayments();
      setShowAddPayment(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeletePayment = async (id) => {
    try {
      const paymentToDelete = payments.find((p) => p.id === id);

      // Show confirmation dialog
      const result = await Swal.fire({
        title: "تأكيد الحذف",
        html: `
                    هل أنت متأكد من حذف المدفوع التالي؟<br>
                    <b>المبلغ:</b> ${paymentToDelete.amount} دينار<br>
                    <b>العميل:</b> ${paymentToDelete.client_name}<br>
                    <b>رقم القضية:</b> ${
                      paymentToDelete.case_number || "غير محدد"
                    }
                `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "نعم، حذف",
        cancelButtonText: "إلغاء",
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return;
      }

      // Show loading indicator
      Swal.fire({
        title: "جاري الحذف...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await paymentsService.deletePayment(id);

      if (response.success) {
        setPayments((prevPayments) =>
          prevPayments.filter((payment) => payment.id !== id)
        );

        Swal.fire({
          title: "تم الحذف!",
          text: "تم حذف المدفوع بنجاح",
          icon: "success",
          timer: 1500,
        });
      } else {
        throw new Error(response.message || "فشل في حذف المدفوع");
      }
    } catch (error) {
      logger.error("Payment deletion error:", error);

      Swal.fire({
        title: "خطأ!",
        text: error.message || "فشل في حذف المدفوع",
        icon: "error",
      });
    }
  };

  // View payment details
  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setViewPaymentDetails(true);
  };

  // Edit payment
  const handleEditPayment = (payment) => {
    setEditPayment(payment);
    setShowEditPayment(true);
  };

  // Handle edit success
  const handleEditSuccess = () => {
    loadPayments();
    setShowEditPayment(false);
    setEditPayment(null);
  };

  const handleAddSuccess = () => {
    loadPayments();
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setFilters({
      status: "all",
      type: "all",
      dateFrom: null,
      dateTo: null,
    });
  };

  // Summary calculations
  const totalAmount =
    filteredPayments?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const completedAmount =
    filteredPayments
      ?.filter((p) => p.status === "completed")
      .reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const pendingAmount =
    filteredPayments
      ?.filter((p) => p.status === "pending")
      .reduce((sum, payment) => sum + payment.amount, 0) || 0;
  const completedCount =
    filteredPayments?.filter((p) => p.status === "completed").length || 0;
  const pendingCount =
    filteredPayments?.filter((p) => p.status === "pending").length || 0;

  // Pagination
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = Array.isArray(filteredPayments)
    ? filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment)
    : [];
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const getPaymentTypeLabel = (type) => {
    const types = {
      case: "قضية",
      consultation: "استشارة",
      other: "أخرى",
    };
    return types[type] || type;
  };

  const getStatusVariant = (status) => {
    return status === "completed" ? "success" : "warning";
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50/30 p-6" dir="rtl">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-start"
          >
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                إدارة المدفوعات
              </h1>
              <p className="text-muted-foreground mt-1">
                إدارة وتتبع المدفوعات والإيصالات
              </p>
            </div>
            <Button onClick={() => setShowAddPayment(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              دفعة جديدة
            </Button>
          </motion.div>

          {/* Summary Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      المدفوعات المكتملة
                    </p>
                    <p className="text-2xl font-bold text-green-600 mt-2">
                      {completedAmount.toLocaleString("ar-EG")} دينار
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {completedCount} مدفوعة
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      المدفوعات المعلقة
                    </p>
                    <p className="text-2xl font-bold text-amber-600 mt-2">
                      {pendingAmount.toLocaleString("ar-EG")} دينار
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {pendingCount} مدفوعة
                    </p>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-full">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      إجمالي المدفوعات
                    </p>
                    <p className="text-2xl font-bold text-blue-600 mt-2">
                      {totalAmount.toLocaleString("ar-EG")} دينار
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {payments.length} مدفوعة
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Search and Filters */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="ابحث بالاسم، الوصف، الرقم أو المبلغ..."
                    className="pr-10"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="gap-2"
                >
                  <Filter className="h-4 w-4" />
                  فلترة
                </Button>
              </div>

              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      الحالة
                    </label>
                    <Select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      <option value="all">الكل</option>
                      <option value="completed">مكتمل</option>
                      <option value="pending">معلق</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      النوع
                    </label>
                    <Select
                      value={filters.type}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                    >
                      <option value="all">الكل</option>
                      <option value="case">قضية</option>
                      <option value="consultation">استشارة</option>
                      <option value="other">أخرى</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      من تاريخ
                    </label>
                    <Input
                      type="date"
                      value={filters.dateFrom || ""}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          dateFrom: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      إلى تاريخ
                    </label>
                    <Input
                      type="date"
                      value={filters.dateTo || ""}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          dateTo: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="md:col-span-4 flex justify-end">
                    <Button
                      variant="outline"
                      onClick={handleResetFilters}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" />
                      إعادة تعيين
                    </Button>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>

          {/* Payments Table */}
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-muted-foreground">
                    جاري تحميل البيانات...
                  </p>
                </div>
              ) : (
                <>
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>رقم الإيصال</TableHead>
                          <TableHead>العميل</TableHead>
                          <TableHead>المبلغ</TableHead>
                          <TableHead>التاريخ</TableHead>
                          <TableHead>النوع</TableHead>
                          <TableHead>الحالة</TableHead>
                          <TableHead>التفاصيل</TableHead>
                          <TableHead className="text-center">
                            الإجراءات
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentPayments && currentPayments.length > 0 ? (
                          currentPayments.map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell className="font-medium">
                                #{payment.id}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">
                                    {payment.client_name || "غير محدد"}
                                  </p>
                                  {payment.case_number && (
                                    <p className="text-xs text-muted-foreground">
                                      قضية: {payment.case_number}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-bold text-blue-600">
                                {payment.amount.toLocaleString("ar-EG")} دينار
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {new Date(payment.date).toLocaleDateString(
                                  "ar-EG"
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {getPaymentTypeLabel(payment.type)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={getStatusVariant(payment.status)}
                                >
                                  {payment.status === "completed"
                                    ? "مكتمل"
                                    : "معلق"}
                                </Badge>
                              </TableCell>
                              <TableCell className="max-w-[200px] truncate text-muted-foreground">
                                {payment.description || "لا توجد تفاصيل"}
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewDetails(payment)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditPayment(payment)}
                                    className="h-8 w-8 p-0"
                                    title="تعديل"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <PaymentReceipt
                                    payment={payment}
                                    showLabel={false}
                                    className="h-8 w-8 p-0"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleDeletePayment(payment.id)
                                    }
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan="8" className="h-40">
                              <div className="flex flex-col items-center justify-center text-muted-foreground">
                                <FileText className="h-12 w-12 mb-4 opacity-50" />
                                <p>لا توجد مدفوعات مطابقة لمعايير البحث</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-6 py-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      {filteredPayments.length !== payments.length ? (
                        <>
                          تم العثور على {filteredPayments.length} نتيجة من أصل{" "}
                          {payments.length} مدفوعة
                        </>
                      ) : (
                        <>إجمالي المدفوعات: {payments.length}</>
                      )}
                    </p>
                    {totalPages > 1 && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>

                        {getPageNumbers().map((number) => (
                          <Button
                            key={number}
                            variant={
                              currentPage === number ? "default" : "outline"
                            }
                            size="sm"
                            onClick={() => setCurrentPage(number)}
                          >
                            {number}
                          </Button>
                        ))}

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Modals */}
          {viewPaymentDetails && (
            <PaymentDetailsModal
              payment={selectedPayment}
              onClose={() => setViewPaymentDetails(false)}
            />
          )}

          {showAddPayment && (
            <AddPaymentModal
              visible={showAddPayment}
              onClose={() => setShowAddPayment(false)}
              onSuccess={handleAddSuccess}
            />
          )}

          {showEditPayment && (
            <EditPaymentModal
              visible={showEditPayment}
              onClose={() => {
                setShowEditPayment(false);
                setEditPayment(null);
              }}
              onSuccess={handleEditSuccess}
              payment={editPayment}
            />
          )}

          {showReceiptDialog && (
            <PaymentReceipt
              payment={selectedPayment}
              onClose={() => setShowReceiptDialog(false)}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Payments;
